import { Injectable } from '@angular/core';
import { ReplaySubject, Subscription } from 'rxjs';

import { ReduxService } from '../../../../services/redux.service';
import { Widget } from '../classes/Widget';
import { InteractivityMediaService } from './interactivity-media.service';
import { InteractivityEditorService } from './interactivity-editor.service';
import { TimelineDataActions } from '../redux/timeline-data-actions';
import { IAnimationKeyFrame, IAnimationData } from '../redux/timeline-data.model';

export interface WidgetAnimated {
	id: string;
	props: any;
}

export interface KeyframeSelection {
	active: any;
	selected: string[];
}

@Injectable()
export class InteractivityWidgetAnimationService {
	public widgetsAnimated: WidgetAnimated[];
	public widgetsAnimatedSubject: ReplaySubject<WidgetAnimated[]>;
	public keyframeSelectionSubject: ReplaySubject<KeyframeSelection>;

	public keyframeSelection: KeyframeSelection = {
		active: {},
		selected: []
	};
	private frameDuration = 0.09; // TODO: update with fps
	private widgets: Widget[];
	private subscriptions: Subscription;
	private currentTime = 0;


	constructor(
		private mediaService: InteractivityMediaService,
		private reduxService: ReduxService,
		private editorService: InteractivityEditorService
	) {
		this.widgetsAnimated = [];
		this.widgetsAnimatedSubject = new ReplaySubject<WidgetAnimated[]>();
		this.keyframeSelectionSubject = new ReplaySubject<KeyframeSelection>(1);
		this.widgets = [];

		this.subscriptions = new Subscription();
		this.subscriptions.add(
			this.mediaService.currentTimeSubject.subscribe((currentTime: number) => {
				this.handleCurrentTimeChange(currentTime);
			})
		);
		this.subscriptions.add(
			this.editorService.widgetsSubject.subscribe((widgets: Widget[]) => {
				this.widgets = widgets;
				this.handleCurrentTimeChange(this.currentTime);
			})
		);
	}
	/**
	 * Search the current data point index
	 *   using binary search, depending on time-sorted data points
	 *
	 * @param data points
	 * @param ts timestamp in seconds
	 * @return current data point index [-1, data.length-1]
	 */
	static searchCurrent(data: any, ts: number): number {
		let n: number = Math.pow(2, Math.ceil(Math.log(data.length) / Math.log(2))); // nearest power of two larger than data.length
		let current = 0;
		let current_inc = 0;
		do {
			n = Math.floor(n / 2);
			current_inc = current + n;
			if (current_inc < data.length && parseFloat(data[current_inc].time) <= ts) {
				current = current_inc;
			}
		} while (n > 0);

		if (!data[0] || ts < parseFloat(data[0].time)) {
			current = -1;
		} // NB!

		return current;
	}

	selectKeyframe(keyframe: string) {
		this.keyframeSelection.selected = [keyframe];
		this.keyframeSelectionSubject.next(this.keyframeSelection);
	}

	clearKeyframeSelection() {
		this.keyframeSelection.selected = [];
		this.keyframeSelectionSubject.next(this.keyframeSelection);
	}

	removeSelectedKeyframe() {
		if (this.keyframeSelection.selected.length) {
			this.widgets.forEach((widget) => {
				if (widget.animations) {
					widget.animations.forEach((animation: IAnimationData) => {
						if (animation && animation.data) {
							animation.data.forEach((keyframe, index) => {
								if (keyframe && keyframe.id && keyframe.id === this.keyframeSelection.selected[0]) {
									this.clearKeyframeSelection();
									this.removeKeyframe(widget.id, index, animation.legend);
								}
							});
						}
					});
				}
			});
		}
	}

	removeKeyframe(widgetId: string, keyframeIndex: number, legend: string) {
		this.reduxService.dispatch({
			type: TimelineDataActions.REMOVE_KEYFRAME,
			id: widgetId,
			keyframeIndex,
			legend,
			description: 'Removing keyframe from animations of widget: ' + widgetId
		});
	}

	toggleKeyframe(widgetId: string, keyframe: IAnimationKeyFrame, legend: string) {
		const active = this.keyframeSelection.active[widgetId];
		if (active && active[legend] > -1) {
			this.removeKeyframe(widgetId, active[legend], legend);
		} else {
			this.reduxService.dispatch({
				type: TimelineDataActions.ADD_KEYFRAME,
				id: widgetId,
				keyframe,
				legend,
				description: 'Added new keyframe to animations of widget: ' + widgetId
			});
		}
	}

	addOrUpdateKeyframe(widgetId: string, value: string, legend: string) {
		const active = this.keyframeSelection.active[widgetId];
		const newKeyframe: IAnimationKeyFrame = { time: String(this.currentTime), value };
		if (active && active[legend] > -1) {
			this.reduxService.dispatch({
				type: TimelineDataActions.CHANGE_KEYFRAME,
				id: widgetId,
				keyframe: newKeyframe,
				keyframeIndex: active[legend],
				legend,
				description: 'Changed keyframe of widget: ' + widgetId
			});
		} else {
			this.reduxService.dispatch({
				type: TimelineDataActions.ADD_KEYFRAME,
				id: widgetId,
				keyframe: newKeyframe,
				legend,
				description: 'Added new keyframe to animations of widget: ' + widgetId
			});
		}
	}

	private handleCurrentTimeChange(currentTime: number, timeForRelative: number = 0): void {
		this.currentTime = currentTime;
		this.widgetsAnimated = [];
		this.keyframeSelection.active = {};
		for (let i = 0, il = this.widgets.length; i < il; i++) {
			const widget: Widget = this.widgets[i];
			if (widget.animations && widget.animations.length > 0) {
				const widgetAnimated: WidgetAnimated = { id: widget.id, props: {} };
				this.keyframeSelection.active[widget.id] = {};
				for (let j = 0, jl = widget.animations.length; j < jl; j++) {
					const anim: any = widget.animations[j];
					if (anim.data && anim.data.length && anim.legend) {
						this.keyframeSelection.active[widget.id][anim.legend] = -1;
						const keys: string[] = anim.legend.split(',');
						const ts: number = (anim.relative === 'true') ? currentTime - timeForRelative : currentTime;


						// binary search for the most recent data point (that has passed)
						const current = InteractivityWidgetAnimationService.searchCurrent(anim.data, ts);

						// left
						let vals_left: string[];
						let time_left: number;
						if (current >= 0) { // 'found'
							vals_left = anim.data[current].value.split(',');
							time_left = parseFloat(anim.data[current].time);
							if (Math.abs(ts - time_left) < this.frameDuration) {
								this.keyframeSelection.active[widget.id][anim.legend] = current;
							}
						} else { // not found => shelve
							vals_left = anim.data[0].value.split(',');
							time_left = 0;
						}

						// right
						let vals_right: string[];
						let time_right: number;
						if (current + 1 < anim.data.length) { // found
							vals_right = anim.data[current + 1].value.split(',');
							time_right = parseFloat(anim.data[current + 1].time);
							if (Math.abs(ts - time_right) < this.frameDuration) {
								this.keyframeSelection.active[widget.id][anim.legend] = current + 1;
							}
						} else { // not found => shelve
							vals_right = anim.data[anim.data.length - 1].value.split(',');
							time_right = ts;
						}

						// interpolate
						const weight: number = (time_right > time_left)
							? Math.max(0.0, Math.min(((ts - time_left) / (time_right - time_left)), 1.0))
							: 1.0; // [ 0.0 - 1.0 ]
						for (let k = 0; k < keys.length; k++) {
							if (vals_left[k] && vals_right[k]) {
								const perc: boolean = (vals_left[k].indexOf('%') >= 0);
								const val_left: number = parseFloat(vals_left[k].replace('%', ''));
								const val_right: number = parseFloat(vals_right[k].replace('%', ''));
								const val_inter: number = (1.0 - weight) * val_left + weight * val_right;
								widgetAnimated.props[keys[k]] = val_inter.toString() + (perc ? '%' : '');
							}
						}
					}
				}

				widget.setAnimatedConfig(widgetAnimated.props);

				this.widgetsAnimated.push(widgetAnimated);
			} else {
				widget.setAnimatedConfig({});
			}
		}
		this.keyframeSelectionSubject.next(this.keyframeSelection);
		this.widgetsAnimatedSubject.next(this.widgetsAnimated);
	}
}
